import { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useApi } from '../lib/api.js';
import { useNavigate } from 'react-router-dom';
import { useLang, LangSwitcher } from '../lib/i18n.js';

export function Onboarding() {
  const { user } = useUser();
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useLang();
  const [businessName, setBusinessName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!user) { setError(t('onb.err.signin')); return; }
    if (!businessName.trim()) { setError(t('onb.err.name')); return; }
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/onboarding/signup', {
        clerk_org_id: `user_${user.id}`,
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName ?? user.firstName ?? 'Owner',
        business_type: 'ecommerce_direct',
        primary_language_id: 'ar-IQ',
        supported_languages: ['ar-IQ', 'en-US'],
        language_mode: 'auto_detect',
        business_name: businessName.trim(),
      });
      navigate('/');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 bg-atmosphere px-6 py-12">
      <div className="max-w-2xl mx-auto relative z-10">
        <div className="flex items-center justify-between mb-8">
          <div className="latin flex items-center gap-2.5 text-lg font-bold">
            <span className="w-2 h-2 rounded-full bg-brand-500 shadow-[0_0_14px_var(--accent-glow)]" />
            ChatRunner
          </div>
          <LangSwitcher />
        </div>

        <h1 className="text-3xl font-extrabold tracking-tight">{t('onb.title')}</h1>
        <p className="text-slate-500 mt-2">{t('onb.subtitle')}</p>

        <section className="mt-8">
          <label className="block text-sm font-medium text-slate-700">{t('onb.businessName')}</label>
          <input
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder={t('onb.businessName.ph')}
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
        </section>

        {error && <div className="mt-4 text-sm text-red-600">{error}</div>}

        <div className="mt-8 flex justify-end">
          <button
            disabled={!businessName.trim() || submitting}
            onClick={submit}
            className="rounded-md bg-brand-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 shadow-[0_10px_40px_-10px_var(--accent-glow)]"
          >
            {submitting ? t('common.creating') : t('common.continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
